/**
 * Authentik authentication provider wrapper for PING.
 *
 * Uses react-oidc-context for generic OIDC authentication with Authentik.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth } from 'react-oidc-context';
import { User, WebStorageStateStore } from 'oidc-client-ts';
import { buildAuthentikOidcConfig, AuthProviderConfig } from './authConfig';

// Context type that matches the Auth0 interface for compatibility
interface AuthentikAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null | undefined;
  error: Error | null;
  loginWithRedirect: (options?: any) => Promise<void>;
  logout: (options?: any) => void;
  getAccessTokenSilently: () => Promise<string>;
}

const AuthentikAuthContext = createContext<AuthentikAuthContextType | null>(null);

interface AuthentikProviderProps {
  children: React.ReactNode;
  config: AuthProviderConfig;
}

/**
 * Inner provider that wraps OIDC context.
 */
function AuthentikAuthInner({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const [authError, setAuthError] = useState<Error | null>(null);

  // Handle authentication errors
  useEffect(() => {
    if (auth.error) {
      console.error('Authentik auth error:', auth.error);
      setAuthError(auth.error);
    }
  }, [auth.error]);

  // Handle post-login redirect
  useEffect(() => {
    if (auth.isAuthenticated && window.location.pathname === '/callback') {
      // Navigate to dashboard after successful login
      window.history.replaceState({}, '', '/');
    }
  }, [auth.isAuthenticated]);

  const loginWithRedirect = useCallback(async (options?: any) => {
    try {
      setAuthError(null);
      await auth.signinRedirect({
        state: options?.appState || { returnTo: window.location.pathname },
      });
    } catch (error) {
      console.error('Authentik login error:', error);
      setAuthError(error as Error);
      throw error;
    }
  }, [auth]);

  const logout = useCallback((options?: any) => {
    const returnTo = options?.logoutParams?.returnTo || window.location.origin;
    auth.signoutRedirect({
      post_logout_redirect_uri: returnTo,
    });
  }, [auth]);

  const getAccessTokenSilently = useCallback(async (): Promise<string> => {
    if (!auth.user?.access_token) {
      // Try to silently refresh
      try {
        await auth.signinSilent();
      } catch (error) {
        console.error('Failed to get Authentik access token:', error);
        throw new Error('Failed to get access token');
      }
    }

    if (!auth.user?.access_token) {
      throw new Error('No access token available');
    }

    return auth.user.access_token;
  }, [auth]);

  const contextValue: AuthentikAuthContextType = {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user,
    error: authError || auth.error || null,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  };

  return (
    <AuthentikAuthContext.Provider value={contextValue}>
      {children}
    </AuthentikAuthContext.Provider>
  );
}

/**
 * Authentik authentication provider component.
 *
 * Wraps children with OIDC authentication context configured for Authentik.
 */
export function AuthentikProvider({ children, config }: AuthentikProviderProps) {
  const oidcConfig = buildAuthentikOidcConfig(config);

  const fullConfig = {
    ...oidcConfig,
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    onSigninCallback: (_user: User | void) => {
      // Clear URL parameters after login
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };

  return (
    <OidcAuthProvider {...fullConfig}>
      <AuthentikAuthInner>{children}</AuthentikAuthInner>
    </OidcAuthProvider>
  );
}

/**
 * Hook to access Authentik authentication context.
 */
export function useAuthentikAuth(): AuthentikAuthContextType {
  const context = useContext(AuthentikAuthContext);
  if (!context) {
    throw new Error('useAuthentikAuth must be used within an AuthentikProvider');
  }
  return context;
}

/**
 * Convert Authentik user to Auth0-like user object for compatibility.
 */
export function normalizeAuthentikUser(user: User | null | undefined): any {
  if (!user || !user.profile) {
    return null;
  }

  return {
    sub: user.profile.sub,
    email: user.profile.email,
    name: user.profile.name || user.profile.preferred_username,
    nickname: user.profile.preferred_username,
    picture: user.profile.picture,
    email_verified: user.profile.email_verified,
    // Authentik specific
    groups: (user.profile as any).groups || [],
    // Map groups to permissions for compatibility
    permissions: mapGroupsToPermissions((user.profile as any).groups || []),
  };
}

/**
 * Map Authentik groups to PING permissions.
 */
function mapGroupsToPermissions(groups: string[]): string[] {
  const permissions: string[] = [];

  const groupMap: Record<string, string[]> = {
    // Terraform-managed Authentik groups
    ping_admingroup: [
      'admin:ping_system', 'read:ping_feedback', 'write:ping_feedback',
      'delete:ping_feedback', 'read:ping_projects', 'write:ping_projects',
      'delete:ping_projects', 'read:ping_organisations', 'write:ping_organisations',
      'delete:ping_organisations', 'read:ping_system_monitoring', 'write:ping_cache',
      'read:ping_analytics', 'write:ping_analytics',
    ],
    ping_readersgroup: [
      'read:ping_feedback', 'read:ping_projects',
      'read:ping_organisations', 'read:ping_analytics',
    ],
    // Legacy group name patterns
    'ping-admins': ['admin:ping_system', 'write:ping_feedback', 'read:ping_feedback'],
    admins: ['admin:ping_system'],
    'ping-readers': ['read:ping_feedback'],
  };

  for (const group of groups) {
    const lowerGroup = group.toLowerCase();
    if (groupMap[lowerGroup]) {
      permissions.push(...groupMap[lowerGroup]);
    }
  }

  return [...new Set(permissions)];
}
